import { useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/lib/routes';
import { useAuth } from '@/hooks/auth/useAuth';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { useTakeoverEscape } from '../TakeoverShell';
import { adminNavItems } from '../NavBar/nav-items';
import { AdminShellView, type AdminNavGroup } from './AdminShellView';

/**
 * Layout route for the Admin takeover: every instance-administration page
 * renders inside this shell, and the app shell drops its sidebar on `/admin`
 * paths (see `isAdminPath`). Back and `esc` return to the org's agent
 * sessions page.
 */
export function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const slug = useActiveOrgSlug();
  const { can, authEnabled, isPlatformAdmin } = useAuth();

  const groups = useMemo<AdminNavGroup[]>(() => {
    const visible = adminNavItems.filter((item) => {
      if (!item.permission) return true;
      if (!authEnabled) return true;
      if (isPlatformAdmin) return true;
      return can(item.permission);
    });
    return [{ label: '', items: visible }];
  }, [can, authEnabled, isPlatformAdmin]);

  const goBack = useCallback(() => {
    const fallback = slug ? ROUTES.org(slug).runs : ROUTES.root;
    void navigate(fallback);
  }, [navigate, slug]);

  useTakeoverEscape(goBack);

  return (
    <AdminShellView groups={groups} activePath={location.pathname} onBack={goBack}>
      <Outlet />
    </AdminShellView>
  );
}
